// src/modules/units/tyres/shared/retread-batch-core.js
//
// The pure core of the retread-batch client: the vocabulary (enums that mirror the
// backend contract) and the two bits of logic worth testing on their own — turning
// a raw HTTP response into one predictable envelope, and deciding what recovery a
// failed operation calls for.
//
// It lives in shared/ (not views/) and imports nothing, for the same reason
// tyre-status.js does: this is decision logic, and decision logic should be
// runnable in a Node harness without a browser, a token, or a live API. The fetch
// layer (views/retread-batch.api.js) is a thin shell over these functions.
//
// ---- why an operation "saga" exists on the batch --------------------------
// The backend runs on standalone MongoDB with no multi-document transactions, so a
// dispatch / receipt / cancel — which moves many tyres, clears sensors, and writes
// history in one logical step — cannot be atomic at the store. Each such operation
// is instead a resumable saga keyed by an operationId: on failure you re-POST the
// SAME endpoint with the SAME payload and it continues from where it stopped
// without double-applying. The operations are SYNCHRONOUS (a 2xx already carries
// the settled batch), so there is nothing to poll — operationState is a
// crash-recovery surface, not an async lifecycle. These functions read that
// surface and tell the caller which of four things to do about a failure.

// ---- vocabulary (mirrors the backend enums) -------------------------------

export const RETREAD_BATCH_STATUS = Object.freeze({
  DRAFT: 'DRAFT',
  OPEN: 'OPEN',
  PARTIALLY_RETURNED: 'PARTIALLY_RETURNED',
  CLOSED: 'CLOSED',
  CANCELLED: 'CANCELLED'
});

export const RETREAD_LINE_STATUS = Object.freeze({
  DRAFT: 'DRAFT',
  OUT: 'OUT',
  RETURNED_RETREADED: 'RETURNED_RETREADED',
  RETURNED_REJECTED: 'RETURNED_REJECTED',
  WRITTEN_OFF: 'WRITTEN_OFF',
  CANCELLED: 'CANCELLED'
});

// The outcome a clerk records for each tyre on a receipt.
export const RETREAD_OUTCOME = Object.freeze({
  RETREADED: 'RETREADED',
  REJECTED: 'REJECTED',
  WRITTEN_OFF: 'WRITTEN_OFF'
});

// Outcomes that require a rejectReason (backend enforces it; the receive form
// mirrors the rule so the user is told before they submit).
export const OUTCOME_NEEDS_REASON = Object.freeze([
  RETREAD_OUTCOME.REJECTED,
  RETREAD_OUTCOME.WRITTEN_OFF
]);

export const OPERATION_STATE = Object.freeze({
  IDLE: 'IDLE',
  IN_PROGRESS: 'IN_PROGRESS',
  FAILED: 'FAILED',
  ROLLING_BACK: 'ROLLING_BACK',
  ROLLED_BACK: 'ROLLED_BACK',
  ROLLBACK_FAILED: 'ROLLBACK_FAILED'
});

export const OPERATION_TYPE = Object.freeze({
  DISPATCH: 'DISPATCH',
  RECEIVE: 'RECEIVE',
  CANCEL: 'CANCEL'
});

// Top-level error code meaning "another operation is already running on an
// affected batch" — distinct from the tyre-eligibility 409s, so the UI can say
// "it's busy, wait" instead of "your selection is wrong".
export const BATCH_OPERATION_CONFLICT = 'BATCH_OPERATION_CONFLICT';

// Per-tyre rejection reasons (in tyreErrors[].reason on a 409). Frozen here so the
// eligibility handling and its copy map reference names, not string literals.
export const RETREAD_TYRE_ERROR = Object.freeze({
  TYRE_NOT_FOUND: 'TYRE_NOT_FOUND',
  TYRE_NOT_IN_STOCK: 'TYRE_NOT_IN_STOCK',
  TYRE_FITTED: 'TYRE_FITTED',
  TYRE_ON_OTHER_BATCH: 'TYRE_ON_OTHER_BATCH',
  TYRE_ALREADY_OUT: 'TYRE_ALREADY_OUT',
  TYRE_DUPLICATE: 'TYRE_DUPLICATE',
  TYRE_NOT_OUT: 'TYRE_NOT_OUT',
  TYRE_NOT_ON_THIS_BATCH: 'TYRE_NOT_ON_THIS_BATCH',
  TYRE_WRONG_SUPPLIER: 'TYRE_WRONG_SUPPLIER',
  REJECT_REASON_REQUIRED: 'REJECT_REASON_REQUIRED'
});

// The one warning code we branch on today (the soft retread cap). Others — e.g.
// the backend's STANDALONE_MONGODB_NON_ATOMIC operational note — arrive in the same
// warnings[] channel and are shown generically, so this is a known-codes list, not
// a closed set.
export const RETREAD_WARNING = Object.freeze({
  RETREAD_CAP_REACHED: 'RETREAD_CAP_REACHED'
});

// What the caller should DO about a failed request.
export const RECOVERY = Object.freeze({
  RETRY: 'RETRY',       // forward recovery — re-POST the same endpoint + payload
  WAIT: 'WAIT',         // an operation is mid-flight; refetch and try again shortly
  ESCALATE: 'ESCALATE', // stuck or forbidden; needs an administrator / support
  NONE: 'NONE'          // not retryable here (e.g. fix the tyre selection instead)
});

// ---- helpers --------------------------------------------------------------

const isArray = Array.isArray;

/**
 * Pull the operation/recovery fields out of a response body. They appear at the
 * top level on error bodies and on the batch object on success bodies, so we look
 * in both. Returns null when the body carries no operation surface at all.
 */
export function extractOperation(body) {
  if (!body || typeof body !== 'object') return null;
  const b = (body.retreadBatch && typeof body.retreadBatch === 'object') ? body.retreadBatch : {};
  const state = body.operationState ?? b.operationState ?? null;
  const type = body.operationType ?? b.operationType ?? null;
  const id = body.operationId ?? b.operationId ?? null;
  const error = body.operationError ?? b.operationError ?? null;
  if (state === null && type === null && id === null && error === null) return null;
  return { id, type, state, error };
}

/**
 * Normalise the two ways a 409 names bad tyres into one list of
 * { externalTyreId, reason }. Prefers the per-tyre tyreErrors[]; falls back to the
 * flat invalidExternalTyreIds[] (reason null), so a caller always has the ids to
 * show even if the richer array is absent.
 */
export function normalizeTyreErrors(body) {
  if (!body || typeof body !== 'object') return [];
  if (isArray(body.tyreErrors) && body.tyreErrors.length) {
    return body.tyreErrors.map((e) => ({
      externalTyreId: e?.externalTyreId ?? null,
      reason: e?.reason ?? null
    }));
  }
  if (isArray(body.invalidExternalTyreIds)) {
    return body.invalidExternalTyreIds.map((id) => ({ externalTyreId: id, reason: null }));
  }
  return [];
}

/**
 * The recovery decision, from the three signals a failure carries. Deliberately
 * driven by operationState (and the conflict code), NOT by operationError — the
 * error string is for display and logging, so the backend can reword or extend it
 * without changing what the UI does.
 */
export function deriveRecovery({ status = 0, code = null, operationState = null } = {}) {
  // Another operation holds the batch. Don't start a different one — wait, refetch.
  if (code === BATCH_OPERATION_CONFLICT) return RECOVERY.WAIT;

  // Permission (e.g. rollback attempted by a non-admin) — nothing to forward-recover.
  if (status === 403) return RECOVERY.ESCALATE;

  switch (operationState) {
    case OPERATION_STATE.FAILED: return RECOVERY.RETRY;             // resume: same endpoint + payload
    case OPERATION_STATE.IN_PROGRESS: return RECOVERY.WAIT;         // busy (rare under sync; cross-tab)
    case OPERATION_STATE.ROLLING_BACK: return RECOVERY.WAIT;        // busy, being undone
    case OPERATION_STATE.ROLLBACK_FAILED: return RECOVERY.ESCALATE; // wedged; needs an admin
    case OPERATION_STATE.ROLLED_BACK: return RECOVERY.NONE;         // undone; user may re-attempt fresh
    default: break;
  }

  // A 5xx with no operation surface is still the contract's retryable failure.
  if (status >= 500) return RECOVERY.RETRY;

  // Everything else (notably eligibility 409s) is not fixed by retrying the same
  // call — the caller shows tyreErrors and lets the user change the selection.
  return RECOVERY.NONE;
}

/**
 * Turn a raw fetch result — { ok, status, body } — into the one envelope every
 * caller and view reads. `replay` (a { method, path, body } descriptor, mutations
 * only) is carried through so a failed operation can be resumed with a
 * byte-identical payload, which is exactly what the saga's idempotent resume needs.
 *
 * Success shape:  { success:true,  status, batch, batches, lines, count, warnings, operation, data, _replay }
 * Failure shape:  { success:false, status, message, code, tyreErrors, invalidTyreIds,
 *                   batch, batches, warnings, operation, recovery, locked, retryable, data, _replay }
 */
export function shapeResult(res, replay = null) {
  const body = res?.body ?? null;
  const warnings = isArray(body?.warnings) ? body.warnings : [];
  const replayDesc = (replay && replay.method)
    ? { method: replay.method, path: replay.path, body: replay.body ?? null }
    : null;

  if (res?.ok) {
    return {
      success: true,
      status: res.status,
      batch: body?.retreadBatch ?? null,
      batches: body?.retreadBatches ?? body?.results ?? null,
      lines: body?.lines ?? null,
      count: typeof body?.count === 'number' ? body.count : null,
      warnings,
      operation: extractOperation(body),
      data: body,
      _replay: replayDesc
    };
  }

  const code = body?.code ?? null;
  const operation = extractOperation(body);
  const recovery = deriveRecovery({ status: res?.status ?? 0, code, operationState: operation?.state ?? null });

  return {
    success: false,
    status: res?.status ?? 0,
    message: body?.message || body?.error || 'Retread batch request failed.',
    code,
    tyreErrors: normalizeTyreErrors(body),
    invalidTyreIds: isArray(body?.invalidExternalTyreIds) ? body.invalidExternalTyreIds : [],
    // the batch(es) the error carried, so a view can reflect current state now
    // without a refetch
    batch: body?.retreadBatch ?? null,
    batches: body?.retreadBatches ?? null,
    warnings,
    operation,
    recovery,
    locked: code === BATCH_OPERATION_CONFLICT,
    retryable: recovery === RECOVERY.RETRY,
    data: body,
    _replay: replayDesc
  };
}
